import { Box, Chip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { ContentAnalysisResults, ContentCheckResult } from '@/types/api/seo';

type Props = {
  results: ContentAnalysisResults;
};

const checkLabels: Record<keyof ContentAnalysisResults, string> = {
  titleTag: 'Title Tag',
  metaDescription: 'Meta Description',
  headings: 'Heading Structure',
  keywordDensity: 'Keyword Density',
  wordCount: 'Word Count',
  readability: 'Readability',
  internalLinks: 'Internal Links',
  externalLinks: 'External Links',
  imageAltText: 'Image Alt Text',
};

const getScoreIcon = (score: number) => {
  if (score >= 80) return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
  if (score >= 50) return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
  return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
};

const getScoreColor = (score: number): 'success' | 'warning' | 'error' => {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'error';
};

const OptimizationChecklistComponent = function ({ results }: Props): JSX.Element {
  const checks = Object.entries(results) as [keyof ContentAnalysisResults, ContentCheckResult][];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {checks.map(([key, check]) => (
        <Box
          key={key}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            padding: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {getScoreIcon(check.score)}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {checkLabels[key]}
              </Typography>
              <Chip
                label={`${check.score}/100`}
                color={getScoreColor(check.score)}
                size="small"
                variant="outlined"
              />
            </Box>
            {check.value !== null && (
              <Typography variant="caption" color="text.secondary">
                Value: {String(check.value)}
              </Typography>
            )}
            {check.issues.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {check.issues.map((issue, i) => (
                  <Typography key={i} variant="caption" color="error.main" display="block">
                    {issue}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default OptimizationChecklistComponent;
